import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, finalize, shareReplay, tap } from 'rxjs/operators';
import { AuthService } from '../auth.service';
import { ReleaseNotesService } from './release-notes.service';
import { IReleaseNote } from '../../models/release-note.model';

const STORAGE_PREFIX = 'ratoeira-release-notes-last-seen';

@Injectable({
    providedIn: 'root',
})
export class ReleaseNotesUnreadService {
    private readonly unreadCountSubject = new BehaviorSubject<number>(0);
    /** Evita vários GETs simultâneos quando refresh() é chamado em sequência. */
    private refreshInFlight?: Observable<number>;

    readonly unreadCount$: Observable<number> = this.unreadCountSubject.asObservable();

    constructor(
        private releaseNotesService: ReleaseNotesService,
        private authService: AuthService,
    ) {}

    refresh(): void {
        // Chamadas concorrentes compartilham o mesmo Observable até o finalize.
        this.refreshInFlight ??= this.releaseNotesService.countUnreadUpdates(this.getLastSeenAt()).pipe(
            catchError(() => of(0)),
            tap((count) => {
                queueMicrotask(() => this.unreadCountSubject.next(count));
            }),
            finalize(() => {
                this.refreshInFlight = undefined;
            }),
            shareReplay({ bufferSize: 1, refCount: true }),
        );

        this.refreshInFlight.subscribe();
    }

    markNotesAsSeen(notes: IReleaseNote[]): void {
        if (typeof localStorage !== 'undefined') {
            const latestMs = notes.reduce((max, note) => {
                const noteMs = new Date(note.created_at).getTime();
                return Number.isFinite(noteMs) ? Math.max(max, noteMs) : max;
            }, 0);
            const seenAt = latestMs > 0 ? new Date(latestMs).toISOString() : new Date().toISOString();
            localStorage.setItem(this.getStorageKey(), seenAt);
        }

        queueMicrotask(() => this.unreadCountSubject.next(0));
    }

    private getLastSeenAt(): string | null {
        if (typeof localStorage === 'undefined') {
            return null;
        }

        return localStorage.getItem(this.getStorageKey());
    }

    private getStorageKey(): string {
        const clientId = this.authService.cliente?.id ?? 'guest';
        return `${STORAGE_PREFIX}-${clientId}`;
    }
}
